export type LabelTranslations = Required<
  Record<
    | Exclude<
        ExpertFormKeys,
        | `config.google_sheets.columns_mapping.${string}` // исключаем проверку вложенных в columns_mapping полей
        | `config.proactive_triggers.${string}` // исключаем вложенные поля проактивных триггеров
      >
    | 'config.google_sheets.columns_mapping' // вручную добавляем обратно только один нужный путь как конечную строку
    | 'config.proactive_triggers' // аналогично для проактивных триггеров
    | 'config.proactive_triggers.timer.message'
    | 'config.proactive_triggers.exit_intent.message'
    | 'config.proactive_triggers.scroll_depth.message'
    | 'config.proactive_triggers.inactivity.message'
    | 'config.proactive_triggers.url_match.message'
    | 'config.proactive_triggers.url_match.url',
    string
  >
>;

export const TRAIN_STATUS = {
  pending: 'pending',
  in_progress: 'in_progress',
  completed: 'completed',
  failed: 'failed',
};

export type TrainStatus = keyof typeof TRAIN_STATUS;

export type Expert = {
  id: string;
  create_date: string;
  update_date?: string;
  user_id: string;
  image_id?: string | null;
  name: string;
  description?: string;
  status?: TrainStatus;
  config: null | ExpertConfig;
};

export type ExpertListItem = Omit<Expert, 'config'> & { is_published: boolean | null };

export type Replacement = [string, string];
export type ReplacementsDict = { [key: string]: string } | undefined;

export type YandexCalendarSettings = {
  caldav_url: string;
  caldav_password: string;
};

export type WidgetSize = 'full' | 'compact';

export type ProactiveTriggerConfig = {
  enabled: boolean;
  message: string;
  delay_seconds?: never;
  url?: never;
};

export type ProactiveTriggersConfig = {
  cooldown_hours: number;
  timer: Omit<ProactiveTriggerConfig, 'delay_seconds'> & { delay_seconds: number };
  exit_intent: ProactiveTriggerConfig;
  scroll_depth: Omit<ProactiveTriggerConfig, 'scroll_pct'> & { scroll_pct: number };
  inactivity: Omit<ProactiveTriggerConfig, 'delay_seconds'> & { delay_seconds: number };
  url_match: Omit<ProactiveTriggerConfig, 'url'> & { url: string };
};

export const DIALOG_EXPORT_KEY = {
  created_at: 'created_at',
  dialog_id: 'dialog_id',
  expert_id: 'expert_id',
  customer_name: 'customer_name',
  customer_phone: 'customer_phone',
  customer_email: 'customer_email',
  task_short: 'task_short',
  task_verbose: 'task_verbose',
  customer_tz: 'customer_tz',
  event_datetime: 'event_datetime',
  transcription: 'transcription',
  field_1: 'field_1',
  field_2: 'field_2',
  field_3: 'field_3',
  field_4: 'field_4',
  field_5: 'field_5',
};

export type DialogExportKey = keyof typeof DIALOG_EXPORT_KEY;

export const DIALOG_EXPORT_KEYS = Object.keys(DIALOG_EXPORT_KEY) as DialogExportKey[];

export type ColumnsMapping = Record<DialogExportKey, { column_name: string; disabled: boolean }>;

export type ExpertConfig = {
  system_prompt?: string;
  system_prompt_voice_chat?: string;
  system_prompt_text_chat?: string;
  dialog_analisys_additional_fields?: string;
  first_sentense?: string;
  temperature?: number;
  use_url_context?: boolean;

  llm?: string;
  max_tokens?: number;
  rag_chunk_size?: number;
  rag_chunk_count?: number;
  use_extended_doc_processing?: boolean;
  use_image_vision?: boolean;
  allow_profanity?: boolean;
  allow_emoji?: boolean;
  allow_offtop?: boolean;
  show_answer_source?: boolean;

  emotion?: string;
  voice?: string;
  speed?: number;

  stt_pause_between_words?: number;

  before_start_text?: string;
  listening_status_text?: string;
  speaking_status_text?: string;
  no_interrupt_speaking_status_text?: string;
  thinking_status_text?: string;
  widget_size?: WidgetSize;
  safe_interrupt?: boolean;

  case_sensitive_replacements?: Replacement[];
  case_insensitive_replacements?: Replacement[];

  yandex_calendar?: YandexCalendarSettings;
  timezone?: string;

  google_sheets?: {
    spreadsheet_url: string;
    columns_mapping: ColumnsMapping;
  };

  tg_tokens?: string[];
  allow_calling_telegram_operator?: boolean;

  allow_calling_supervisor?: boolean;

  mcp_servers?: Record<string, { url: string; transport: string }>;

  suggested_questions?: string[];
  use_suggested_questions?: boolean;

  proactive_triggers?: ProactiveTriggersConfig;

  is_published?: boolean;
};

export type Dictor = {
  emotions: string[];
  translation: { en: string; ru: string };
};

export type LlmDescr = {
  name: string;
  group: 'gemini' | 'yandex' | 'deepseek' | 'qwen' | 'openai';
  vision?: boolean;
  meta: { context_window: string; speed: number; intelligence: number };
};

export type ExpertConfigOptions = {
  llms: LlmDescr[];
  emotions_map: Record<string, { en: string; ru: string }>;
  dictors_map: Record<string, Dictor>;
};

export type ExpertDefaults = { image_id: string } & Required<ExpertConfig> & ExpertConfigOptions;

export type TDialogMessageRole = 'system' | 'user' | 'assistant';

export type ExpertForm = Expert & {
  config: Required<ExpertConfig>;
  image_id: string;
};

type Join<K, P> = K extends string
  ? P extends string
    ? `${K}${'' extends P ? '' : '.'}${P}`
    : never
  : never;

type FlattenKeys<T> =
  T extends Record<string, any>
    ? {
        [K in keyof T]-?: K extends string
          ? T[K] extends Record<string, any>
            ? T[K] extends any[]
              ? K
              : Join<K, FlattenKeys<T[K]>>
            : K
          : never;
      }[keyof T]
    : never;

type ExcludeKeys<T, K extends string> = T extends K ? never : T;

export type ExpertFormKeys = ExcludeKeys<
  FlattenKeys<ExpertForm>,
  'id' | 'create_date' | 'update_date' | 'user_id' | 'status' | 'description'
>;
